import { Column, Columns } from "@r8er/components/marketing/columns";
import { Container } from "@r8er/components/marketing/container";
import { Content } from "@r8er/components/marketing/content";
import { Cta } from "@r8er/components/marketing/cta";
import { Hero } from "@r8er/components/marketing/hero";
import { Layout } from "@r8er/components/marketing/layout";
import { Newsletter } from "@r8er/components/marketing/newsletter";
import { TeamBlockLarge } from "@r8er/components/marketing/team";
import teamBlockClasses from "@r8er/components/marketing/team/team.module.css";
import { getAllR8ERs } from "@r8er/lib/cms";
import { InferGetStaticPropsType } from "next";
import Head from "next/head";

const Watermark = () => (
  <svg
    style={{
      position: "absolute",
      top: "5rem",
      left: 0,
      transform: "translate(-35%,0)",
      opacity: "50%",
      zIndex: 1,
    }}
    xmlns="http://www.w3.org/2000/svg"
    width="649.6"
    height="1051.7"
  >
    <path
      fill="#f0f0f0"
      d="M516 79a270 270 0 00-382 0 270 270 0 00-23 356l49-48c-56-79-49-190 22-260a203 203 0 01286 0c79 79 79 207 0 286L325 556l-74-73-48 47 122 122 191-191a270 270 0 000-382"
    />
    <path
      fill="#f0f0f0"
      d="M555 497l-15-14-48 47 15 15a258 258 0 010 364 258 258 0 01-364 0 258 258 0 010-364l182-182 74 74 48-48-122-122L95 497a325 325 0 000 460 325 325 0 00460 0 325 325 0 000-460"
    />
  </svg>
);

export async function getStaticProps() {
  const r8ers = await getAllR8ERs();

  return {
    props: {
      r8ers,
    },
  };
}

export default function About({
  r8ers,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  return (
    <div>
      <Head>
        <title>Our Team - R8ER</title>
        <link rel="icon" href="/favicon.png" />
      </Head>

      <Layout>
        <Hero isSlim title="Our Team" img="/team-hero.png" />

        <Content isFullWidth isCentered isNarrow>
          <p
            className="p--standard"
            style={{
              fontSize: "calc(1.75 * var(--font-size-base))",
              margin: "0 auto",
              letterSpacing: "-.25px",
            }}
          >
            R8ER's leadership team is a balanced mix of highly qualified and
            experienced professionals, whose talents and knowledge of both the
            medical, computer science and financial industries made them ideal
            for R8ER's long-term vision and mission.
          </p>
        </Content>

        <Container width={780}>
          <TeamBlockLarge
            name="Dr. Milla Marinova"
            title="Founder &amp; Chief Executive Officer"
            imageUrl="/team-milla.png"
          >
            <p>
              With over eight years' experience as a medical doctor, Milla holds
              an M.Sc. in International Health Policy from LSE and an M.Phil. in
              Bioscience Enterprise from Cambridge University.
            </p>
            <p>
              After completing her medical training, Milla secured several key
              positions in various institutes including as an Honorary Research
              Fellow at Imperial College London, as an external consultant to
              McKinsey and is currently contributing to Scottish government
              healthcare reform work.
            </p>
            <p>
              Having worked with the Royal College of Physicians of Edinburgh in
              partnership with the WHO on quality governance in international
              healthcare, she then went on to become principal with GrayBella
              Capital, a growth-capital VC investing in European health
              companies.
            </p>
          </TeamBlockLarge>

          <TeamBlockLarge
            name="Mr. Pavel Marinov"
            title="Chief Operating Officer"
            imageUrl="/team-pavel.png"
          >
            <p>
              After graduating with a B.Sc. in Computer Science with Management
              from King's College London, Pavel became a Churchill Next General
              Leadership Development Fellow before taking roles with Cisco
              Systems as a project manager and development supervisor and at
              Cornucopia Capital as an investment analyst.
            </p>
            <p>
              In addition to his co-founding role of R8ER, Pavel is also the
              founder and director of Navic Ltd, a home automation business
              specializing in consulting for IT, AV and security solutions.
            </p>
          </TeamBlockLarge>
        </Container>

        <div style={{ position: "relative", overflow: "hidden" }}>
          <Watermark />

          <Content isFullWidth isLightGrey containerWidth={1264}>
            <h2 className="h2--underlined">Advisory Board</h2>
            <Columns count={2}>
              <Column removeInner>
                <TeamBlockLarge
                  name="Professor Christopher Lowe OBE"
                  imageUrl="/team-christopher.png"
                  columnStructure="169px auto"
                  classList={[
                    teamBlockClasses["marketing__teamblocklarge--smaller"],
                  ]}
                >
                  <p>
                    Equipped with over forty years of experience in the field of
                    healthcare biotechnology, Professor Lowe is not only one of
                    Britain's finest academics, but also recognized
                    internationally as the "Most Entrepreneurial Scientist of
                    the UK".
                  </p>
                  <p>
                    With more than four hundred publications, one hundred
                    patents and multiple spin outs, Professor Lowe is the
                    recipient of more than twenty major national and
                    international awards including Order of the British Empire.
                  </p>
                  <p>
                    In addition to his position as a R8ER advisor, he continues
                    to serve as both Emeritus Professor of Biotechnology and
                    Director of Cambridge Academy of Therapeutic Sciences at the
                    University of Cambridge.
                  </p>
                </TeamBlockLarge>
                <TeamBlockLarge
                  name="Professor Derek Bell"
                  imageUrl="/team-derek.png"
                  columnStructure="169px auto"
                  classList={[
                    teamBlockClasses["marketing__teamblocklarge--smaller"],
                  ]}
                >
                  <p>
                    Recognized as the first UK Professor of Acute Internal
                    Medicine at Imperial College, Professor Bell has dedicated
                    himself to a career focused on respiratory, general medicine
                    and acute care.
                  </p>
                  <p>
                    Having recently left his position as President of the Royal
                    College of Physicians of Edinburgh and director of the NIHR
                    CLAHRC for Northwest London, Imperial College, Professor
                    Bell continues as an NIHR Senior Investigator and is special
                    advisor to the Scottish Government.
                  </p>
                  <p>
                    With more than 180 papers published and having ran multiple
                    national collaboratives, he is also the recipient of an OBE
                    for services to unscheduled care and quality improvement.
                  </p>
                </TeamBlockLarge>
              </Column>
              <Column removeInner>
                <TeamBlockLarge
                  name="Mr. Steve Barber"
                  imageUrl="/team-steve.png"
                  columnStructure="169px auto"
                  classList={[
                    teamBlockClasses["marketing__teamblocklarge--smaller"],
                  ]}
                >
                  <p>
                    As current Senior Independent Director of National World PLC
                    and a past non-executive director and chairman of the audit
                    committees of Next PLC, the AA pLC, Domino's Pizza PLC, Intu
                    Shopping Centres PLC, Helphire PLC and Fenwick Limited,
                    Steve brings a wealth of experience as a highly respected
                    businessman and advisor. Having previously served as
                    Co-founder of AFC Energy PLC and the Objectivity Partnership
                    LLP, he was also the past chairman of Fenwick Limited and
                    the Design Objectives Group, past CFO of Mirror Group PLC
                    and Whitehead Mann PLC. As one of R8ERs advisors, his
                    experience as a senior partner at Price Waterhouse, as well
                    as running and overseeing highly scalable businesses will
                    add immense value to both investors and entrepreneurs alike.{" "}
                  </p>
                </TeamBlockLarge>
                <TeamBlockLarge
                  name="Prof Alan Barrell"
                  imageUrl="/team-alan.png"
                  columnStructure="169px auto"
                  classList={[
                    teamBlockClasses["marketing__teamblocklarge--smaller"],
                  ]}
                >
                  <p>
                    Alan has worked in Health Care & Medical Research, as
                    Chairman and Chief Executive of large multi-national
                    companies and smaller technology start-ups. He teaches in
                    Universities in UK, Europe, North America & Asia with
                    Professorships in European & Chinese Universities. Raised &
                    managed a Venture Capital Fund, Business Angel Investor &
                    Trustee of charities.
                  </p>
                  <p>
                    He was honoured with The Queen’s Award for Enterprise
                    Promotion in the UK & membership as Knight First Class of
                    the Order of the White Rose of Finland for services to
                    Education. His last full-time position was as Director of
                    Studies at Cambridge Innovation Academy.
                  </p>
                </TeamBlockLarge>
              </Column>
            </Columns>
          </Content>
        </div>
        {/* 
        <OurPartners items={r8ers}>
          <h2 className="h2--standard">Our Partners</h2>
        </OurPartners> */}

        <Cta
          title="Start your R8ER Journey Today"
          description="If you are are looking for investment or looking to invest, sign up to R8ER today."
          buttonText="Get Started"
          buttonHref="/auth/signup"
          isBlue
        />

        <Newsletter />
      </Layout>
    </div>
  );
}
